import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import toast from "react-hot-toast";
import BRAND from "../../brand";
import Icon, { navIconName } from "../Icons";

const MODERN = BRAND.dashboard === "modern";

export default function DashboardLayout({ navItems, children, sectionLabel }) {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const styles = getStyles(isDark);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Signed out successfully.");
  };

  return (
    <div style={styles.shell}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, width: collapsed ? "64px" : "240px" }}>
        {/* Logo */}
        <div style={styles.sidebarLogo}>
          <div style={styles.logoMark}>
            {BRAND.logoMark ? (
              <img src={`${process.env.PUBLIC_URL}/${BRAND.logoMark}`} alt={BRAND.name} style={{ height: "32px", width: "auto", display: "block" }} />
            ) : (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none" style={{ display: "block" }}>
              <rect width="32" height="32" rx="8" fill="#408DC9"/>
              <path d="M8 16C8 11.58 11.58 8 16 8V12C13.79 12 12 13.79 12 16C12 18.21 13.79 20 16 20V24C11.58 24 8 20.42 8 16Z" fill="white"/>
              <path d="M16 8C20.42 8 24 11.58 24 16H20C20 13.79 18.21 12 16 12V8Z" fill="#B3D3EC"/>
              <circle cx="20" cy="20" r="3" fill="#D5E7F5"/>
            </svg>
            )}
          </div>
          {!collapsed && (
            <div>
              <div style={styles.logoName}>{BRAND.short}</div>
              <div style={styles.logoRole}>{sectionLabel}</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(p => !p)}
            style={styles.collapseBtn}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              style={({ isActive }) => ({
                ...styles.navLink,
                ...(isActive ? styles.navLinkActive : {}),
              })}
              title={collapsed ? item.label : undefined}
            >
              <span style={styles.navIcon}>
                {MODERN && navIconName(item) ? <Icon name={navIconName(item)} size={18} /> : item.icon}
              </span>
              {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Theme toggle + User info + logout */}
        <div style={styles.sidebarFooter}>
          <button
            onClick={toggleTheme}
            style={styles.themeToggleBtn}
            title={isDark ? "Switch to light theme" : "Switch to dark theme"}
          >
            {MODERN ? <Icon name={isDark ? "sun" : "moon"} size={16} /> : <span>{isDark ? "☀️" : "🌙"}</span>}
            {!collapsed && <span>{isDark ? "Light theme" : "Dark theme"}</span>}
          </button>

          {!collapsed && (
            <div style={styles.userBlock}>
              <div style={styles.userAvatar}>
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div style={styles.userInfo}>
                <div style={styles.userName}>{user?.name}</div>
                <div style={styles.userEmail}>{user?.email}</div>
              </div>
            </div>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn} title="Sign out">
            {MODERN ? <Icon name="logout" size={16} /> : <span>⎋</span>}
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
}

function getStyles(isDark) {
  const s = getBaseStyles(isDark);
  if (!MODERN) return s;
  const P = BRAND.colorPrimary;
  const A = BRAND.colorAccent;
  // The sidebar follows the theme: brand navy in dark mode, white in light mode.
  const NAVY = "#07182A";
  const d = isDark;
  const text   = d ? "#FFFFFF" : "#0F1E3D";
  const soft   = d ? "rgba(255,255,255,0.78)" : "#475569";
  const muted  = d ? "rgba(255,255,255,0.6)" : "#64748B";
  const line   = d ? "rgba(255,255,255,0.10)" : "#E2E8F0";
  const chip   = d ? "rgba(255,255,255,0.08)" : "#F1F5F9";
  const chipBd = d ? "rgba(255,255,255,0.15)" : "#E2E8F0";
  return {
    ...s,
    sidebar: { ...s.sidebar, background: d ? NAVY : "#FFFFFF", borderRight: d ? "none" : `1px solid ${line}` },
    sidebarLogo: { ...s.sidebarLogo, borderBottom: `1px solid ${line}` },
    logoName: { ...s.logoName, color: text },
    logoRole: { ...s.logoRole, color: d ? A : "#5E7C00" },
    collapseBtn: { ...s.collapseBtn, background: chip, border: `1px solid ${chipBd}`, color: text },
    navLink: { ...s.navLink, color: soft, padding: "11px 12px", borderRadius: "10px", fontSize: "14px" },
    navLinkActive: { background: P, color: "#FFFFFF", boxShadow: d ? "0 4px 14px rgba(0,0,0,0.25)" : "0 4px 12px rgba(64,141,201,0.35)" },
    sidebarFooter: { ...s.sidebarFooter, borderTop: `1px solid ${line}` },
    themeToggleBtn: { ...s.themeToggleBtn, background: chip, border: `1px solid ${chipBd}`, color: text },
    userBlock: { ...s.userBlock, background: chip },
    userAvatar: { ...s.userAvatar, background: A, color: "#0B2236" },
    userName: { ...s.userName, color: text },
    userEmail: { ...s.userEmail, color: muted },
    logoutBtn: { ...s.logoutBtn, color: d ? "rgba(255,255,255,0.7)" : "#64748B" },
  };
}

function getBaseStyles(isDark) {
  return {
    shell: {
      display: "flex",
      minHeight: "100vh",
      background: isDark ? "#060D1F" : "#F3F7FF",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    },
    sidebar: {
      background: isDark ? "rgba(10,16,35,0.98)" : "#FFFFFF",
      borderRight: isDark ? "1px solid rgba(64,141,201,0.15)" : "1px solid #E2E8F0",
      display: "flex",
      flexDirection: "column",
      transition: "width 0.2s ease",
      flexShrink: 0,
      position: "sticky",
      top: 0,
      height: "100vh",
      overflow: "hidden",
    },
    sidebarLogo: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "20px 16px 16px",
      borderBottom: isDark ? "1px solid rgba(64,141,201,0.1)" : "1px solid #EEF2F7",
      position: "relative",
    },
    logoMark: { flexShrink: 0, lineHeight: 0 },
    logoImg: { width: "28px", height: "28px", borderRadius: "7px", objectFit: "cover", display: "block" },
    logoName: {
      fontSize: "16px",
      fontWeight: "700",
      color: isDark ? "#fff" : "#0F1E3D",
      letterSpacing: "-0.3px",
      lineHeight: "1.2",
    },
    logoRole: {
      fontSize: "10px",
      color: "#408DC9",
      fontWeight: "600",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
    collapseBtn: {
      position: "absolute",
      right: "8px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "rgba(64,141,201,0.1)",
      border: "1px solid rgba(64,141,201,0.2)",
      borderRadius: "6px",
      color: "#408DC9",
      width: "22px",
      height: "22px",
      cursor: "pointer",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
    },
    nav: {
      flex: 1,
      padding: "12px 8px",
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      overflowY: "auto",
    },
    navLink: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "9px 10px",
      borderRadius: "8px",
      textDecoration: "none",
      color: isDark ? "#64748B" : "#64748B",
      fontSize: "14px",
      fontWeight: "500",
      transition: "background 0.15s, color 0.15s",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    navLinkActive: {
      background: isDark ? "rgba(64,141,201,0.15)" : "rgba(64,141,201,0.08)",
      color: isDark ? "#8CBCE0" : "#408DC9",
    },
    navIcon: {
      fontSize: "17px",
      flexShrink: 0,
      width: "20px",
      textAlign: "center",
    },
    navLabel: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    sidebarFooter: {
      padding: "12px 8px",
      borderTop: isDark ? "1px solid rgba(64,141,201,0.1)" : "1px solid #EEF2F7",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    themeToggleBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 10px",
      borderRadius: "8px",
      background: isDark ? "rgba(245,158,11,0.08)" : "rgba(64,141,201,0.06)",
      border: isDark ? "1px solid rgba(245,158,11,0.2)" : "1px solid rgba(64,141,201,0.12)",
      cursor: "pointer",
      color: isDark ? "#FBBF24" : "#408DC9",
      fontSize: "13px",
      fontWeight: "600",
      width: "100%",
    },
    userBlock: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 10px",
      borderRadius: "8px",
      background: isDark ? "var(--input-bg)" : "#F1F5F9",
    },
    userAvatar: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      background: "linear-gradient(135deg, #408DC9, #7C3AED)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "13px",
      fontWeight: "700",
      color: "#fff",
      flexShrink: 0,
    },
    userInfo: { minWidth: 0 },
    userName: {
      fontSize: "13px",
      fontWeight: "600",
      color: isDark ? "#E2E8F0" : "#0F1E3D",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    userEmail: {
      fontSize: "11px",
      color: isDark ? "#475569" : "#94A3B8",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    logoutBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 10px",
      borderRadius: "8px",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: isDark ? "#475569" : "#94A3B8",
      fontSize: "13px",
      fontWeight: "500",
      width: "100%",
      transition: "background 0.15s, color 0.15s",
    },
    main: {
      flex: 1,
      overflow: "auto",
      minHeight: "100vh",
    },
  };
}
